import { Box, ButtonBase, Grid, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslate } from "react-admin";

import { LearningMedia, LearningTemplatePage } from "../../../lib/learning/contentContract";
import { resolveLearningMediaUrl } from "../../../lib/learning/mediaReference";
import { borderRadius, color, shadows, space } from "../../../theme/designTokens";
import { TemplateBlock } from "./TemplateBlock";

type FlipCard = {
  id: string;
  title: string;
  icon?: LearningMedia;
  frontImage: LearningMedia;
  back: LearningTemplatePage;
};

const flipCardSpacing = `${space.xl}px`;

export const FlipCardsBlock = ({ cards }: { cards: FlipCard[] }) => {
  const translate = useTranslate();
  const [flipped, setFlipped] = useState<Set<string>>(() => new Set());

  const toggle = (id: string): void => {
    setFlipped(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Grid container spacing={flipCardSpacing}>
      {cards.map(card => {
        const isFlipped = flipped.has(card.id);
        return (
          <Grid item xs={12} sm={6} md={4} key={card.id}>
            <Box
              data-testid={`learning-flip-card-container-${card.id}`}
              sx={{
                width: "100%",
                minHeight: 260,
                p: flipCardSpacing,
                border: `1px solid ${isFlipped ? color.primary : color.panelBorder}`,
                borderRadius: borderRadius.md,
                boxShadow: shadows.card,
                textAlign: "left",
              }}
            >
              <ButtonBase
                data-testid={`learning-flip-card-${card.id}`}
                aria-pressed={isFlipped}
                aria-label={translate(isFlipped ? "learning.flipCards.showFront" : "learning.flipCards.showBack", {
                  title: card.title,
                })}
                onClick={() => toggle(card.id)}
                sx={{ display: "flex", width: "100%", textAlign: "left", alignItems: "stretch" }}
              >
                {isFlipped ? (
                  <Stack spacing={flipCardSpacing} justifyContent="center" alignItems="center" width="100%">
                    {card.icon && (
                      <Box component="img" src={resolveLearningMediaUrl(card.icon.fn)} alt="" width={56} height={56} />
                    )}
                    <Typography component="h3" variant="h6">
                      {card.title}
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={flipCardSpacing} width="100%">
                    <Box
                      component="img"
                      src={resolveLearningMediaUrl(card.frontImage.fn)}
                      alt={card.frontImage.alt ?? ""}
                      sx={{ width: "100%", height: 170, objectFit: "cover", borderRadius: borderRadius.sm }}
                    />
                    <Typography component="h3" variant="h6">
                      {card.title}
                    </Typography>
                  </Stack>
                )}
              </ButtonBase>
              {isFlipped && (
                <Box mt={flipCardSpacing}>
                  <TemplateBlock page={card.back} />
                </Box>
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};
